import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { User, Loader2 } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

export default function ProfileCompletionModal({ isOpen, user, onComplete }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    surname: user?.surname || "",
    gender: user?.gender || "",
    birthDay: "",
    birthMonth: "",
    birthYear: "",
    phone: user?.phone || "",
  });

  // Check which fields are missing
  const missingFields = {
    name: !user?.name || user.name.trim() === "",
    surname: !user?.surname || user.surname.trim() === "",
    gender: !user?.gender,
    birth_date: !user?.birth_date,
    phone: !user?.phone || user.phone.trim() === "",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error("Ad zorunludur");
      return;
    }
    if (!formData.surname.trim()) {
      toast.error("Soyad zorunludur");
      return;
    }
    if (!formData.gender) {
      toast.error("Cinsiyet zorunludur");
      return;
    }
    if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) {
      toast.error(t('common:birthDateRequired'));
      return;
    }
    if (!formData.phone.trim()) {
      toast.error(t('common:phoneRequired'));
      return;
    }

    setLoading(true);

    try {
      const birthDate = `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`;
      
      const response = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
        },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          surname: formData.surname,
          gender: formData.gender,
          birth_date: birthDate,
          phone: formData.phone,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t("common:profileUpdateFailed"));
      }

      const updatedUser = await response.json();
      toast.success(t("common:toast_profilinizBasariylaTamamlandi"));
      onComplete(updatedUser);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mx-auto mb-2">
            <User className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-center">{t("common:profiliniziTamamlayin")}</DialogTitle>
          <DialogDescription className="text-center">
            Devam etmek için lütfen aşağıdaki bilgileri doldurun.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Name & Surname */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="name">Ad <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder={t("common:ph_adiniz")}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-11 rounded-xl"
                disabled={!missingFields.name}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label htmlFor="surname">Soyad <span className="text-red-500">*</span></Label>
              <Input
                id="surname"
                placeholder={t("common:ph_soyadiniz")}
                value={formData.surname}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                className="h-11 rounded-xl"
                disabled={!missingFields.surname}
                data-testid="profile-surname-input"
              />
            </div>
          </div>

          {/* Gender */}
          <div>
            <Label htmlFor="gender">Cinsiyet <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.gender} 
              onValueChange={(value) => setFormData({ ...formData, gender: value })}
              disabled={!missingFields.gender}
            >
              <SelectTrigger data-testid="profile-gender-select" className="h-11 rounded-xl">
                <SelectValue placeholder={t("common:ph_secin")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t('common:male')}</SelectItem>
                <SelectItem value="female">{t('common:female')}</SelectItem>
                <SelectItem value="other">{t('common:other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Birth Date */}
          <div>
            <Label>Doğum Tarihi <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Select 
                value={formData.birthDay} 
                onValueChange={(value) => setFormData({ ...formData, birthDay: value })}
                disabled={!missingFields.birth_date}
              >
                <SelectTrigger className="h-11 rounded-xl flex-1">
                  <SelectValue placeholder={t("common:day")} />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={formData.birthMonth} 
                onValueChange={(value) => setFormData({ ...formData, birthMonth: value })}
                disabled={!missingFields.birth_date}
              >
                <SelectTrigger className="h-11 rounded-xl flex-1">
                  <SelectValue placeholder={t("common:month")} />
                </SelectTrigger>
                <SelectContent>
                  {[t('common:january'), t('common:february'), t('common:march'), t('common:april'), t('common:may'), t('common:june'), t('common:july'), t('common:august'), t('common:september'), t('common:october'), t('common:november'), t('common:december')].map((month, i) => (
                    <SelectItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={formData.birthYear} 
                onValueChange={(value) => setFormData({ ...formData, birthYear: value })}
                disabled={!missingFields.birth_date}
              >
                <SelectTrigger className="h-11 rounded-xl flex-1">
                  <SelectValue placeholder={t("common:year")} />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 80 }, (_, i) => {
                    const year = new Date().getFullYear() - 10 - i;
                    return (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone">Telefon <span className="text-red-500">*</span></Label>
            <Input
              id="phone"
              placeholder="05XX XXX XX XX"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="h-11 rounded-xl"
              disabled={!missingFields.phone}
              data-testid="profile-phone-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-navy hover:bg-navy/90 rounded-xl font-semibold"
            disabled={loading}
            data-testid="profile-complete-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              "Profili Tamamla"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Helper function to check if profile completion is needed
// Only for social login users (no password) who have incomplete profiles
export function isProfileComplete(user) {
  if (!user) return true; // No user = no need to show modal
  
  // If user has a password hash, they registered with email - don't force profile completion
  // This is for users who registered via email/password
  if (user.password_hash || user.has_password) {
    return true;
  }
  
  // For social login users (Google, Phone, Apple), check required fields
  const requiredFields = ['name', 'surname', 'gender', 'birth_date', 'phone'];
  
  for (const field of requiredFields) {
    const value = user[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return false;
    }
  }
  
  return true;
}

// Check if user needs profile completion (for new social login users)
export function needsProfileCompletion(user, isNewUser = false) {
  if (!user) return false;
  
  // Only show for new social login users
  if (!isNewUser) return false;
  
  // If user has password, they're email users - no need
  if (user.password_hash || user.has_password) return false;
  
  // Check if profile is incomplete
  const requiredFields = ['name', 'surname', 'gender', 'birth_date', 'phone'];
  for (const field of requiredFields) {
    const value = user[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return true;
    }
  }
  
  return false;
}
